import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PeriodFilter, HRMultiFilter, presetRange, useHROptions } from "@/components/PeriodFilter";
import { Briefcase } from "lucide-react";

const KPI_LABELS = [
  ["candidates_added", "Candidates Added"],
  ["screening", "Screening"],
  ["interviews_scheduled", "Interviews"],
  ["l1_completed", "L1 Done"],
  ["l2_completed", "L2 Done"],
  ["selected", "Selected"],
  ["joined", "Joined"],
  ["rejected", "Rejected"],
  ["no_show", "No Show"],
  ["open_requirements", "Open Reqs"],
  ["overdue_requirements", "Overdue Reqs"],
  ["hold_requirements", "On Hold"],
  ["concerns_received", "Concerns In"],
  ["concerns_closed", "Concerns Closed"],
  ["concerns_pending", "Concerns Open"],
];

const PERIOD_LABEL = { today: "Today", yesterday: "Yesterday", last7: "Last 7 Days", week: "This Week", month: "This Month", lastmonth: "Last Month", custom: "Custom Range", all: "All Time" };

// Can be controlled from the Dashboard (shared global filters) or run standalone with its own filters
export const DailyHRWork = ({ controlled = false, preset: cPreset, range: cRange, hrSel: cHrSel }) => {
  const { user } = useAuth();
  const [iPreset, setIPreset] = useState("today");
  const [iRange, setIRange] = useState(presetRange("today"));
  const [iHrSel, setIHrSel] = useState([]);
  const [data, setData] = useState(null);
  const canSeeHR = ["super_admin", "hr_manager", "branch_manager"].includes(user?.role);
  const users = useHROptions(canSeeHR && !controlled);

  const preset = controlled ? cPreset : iPreset;
  const range = controlled ? cRange : iRange;
  const hrSel = controlled ? cHrSel : iHrSel;

  useEffect(() => {
    const from = range.from || "2000-01-01";
    const to = range.to || "2099-12-31";
    const hrParam = Array.isArray(hrSel) && hrSel.length ? `&hr=${hrSel.join(",")}` : "";
    api.get(`/reports/activity?from_date=${from}&to_date=${to}${hrParam}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [range, hrSel]);

  const k = data?.kpis || {};
  const kpiLabels = canSeeHR ? KPI_LABELS : KPI_LABELS.filter(([key]) => !key.startsWith("concerns_"));
  const filtersApplied = !!range.from || (Array.isArray(hrSel) && hrSel.length > 0);
  const noData = data && filtersApplied && Object.values(k).every((v) => !v) && (data.hr_wise || []).length === 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg" data-testid="daily-hr-work">
      <div className="px-6 py-4 border-b border-slate-100 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Daily HR Work</div>
          <div className="font-heading font-semibold text-[var(--yc-navy)] flex items-center gap-2">
            <Briefcase className="h-4 w-4 text-[var(--yc-emerald)]" />
            HR Work — {PERIOD_LABEL[preset]}
            {range.from && <span className="text-xs font-normal text-slate-500">({range.from}{range.to !== range.from ? ` → ${range.to}` : ""})</span>}
          </div>
        </div>
        {!controlled && (
          <div className="flex flex-wrap items-end gap-2">
            <PeriodFilter preset={iPreset} onPreset={setIPreset} range={iRange} onRange={setIRange} testId="work-period-filter" />
            {canSeeHR && <HRMultiFilter value={iHrSel} onChange={setIHrSel} users={users} testId="work-hr-filter" />}
          </div>
        )}
      </div>

      {noData && (
        <div className="px-6 py-4 text-sm text-slate-500 bg-amber-50 border-b border-amber-100" data-testid="no-data-message">
          No data found for selected filters.
        </div>
      )}

      <div className="grid grid-cols-3 md:grid-cols-6 gap-px bg-slate-100">
        {kpiLabels.map(([key, label]) => (
          <div key={key} className="bg-white px-4 py-3" data-testid={`work-kpi-${key}`}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className={`font-heading text-xl font-bold mt-0.5 ${key === "overdue_requirements" && k[key] > 0 ? "text-[var(--yc-rose)]" : "text-[var(--yc-navy)]"}`}>
              {k[key] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {canSeeHR && (
        <div className="border-t border-slate-100">
          <div className="px-6 py-3 label-eyebrow">HR-wise work for this period</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="data-cell">HR</th>
                  <th className="data-cell">Added</th>
                  <th className="data-cell">Screening</th>
                  <th className="data-cell">Interviews</th>
                  <th className="data-cell">L1</th>
                  <th className="data-cell">L2</th>
                  <th className="data-cell">Selected / Hiring Closed</th>
                  <th className="data-cell">Joined</th>
                  <th className="data-cell">Pending</th>
                  <th className="data-cell">Rej / No-show</th>
                  <th className="data-cell">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data?.hr_wise || []).length === 0 && (
                  <tr><td colSpan={11} className="data-cell text-center text-slate-500 py-6">No HR activity in this period.</td></tr>
                )}
                {(data?.hr_wise || []).map((h) => (
                  <tr key={h.hr_id} className="border-b border-slate-100 data-row" data-testid={`hr-work-row-${h.hr_id}`}>
                    <td className="data-cell font-medium text-[var(--yc-navy)]">{h.hr_name}</td>
                    <td className="data-cell font-mono">{h.added ?? 0}</td>
                    <td className="data-cell font-mono">{h.screening}</td>
                    <td className="data-cell font-mono">{h.interviews}</td>
                    <td className="data-cell font-mono">{h.l1}</td>
                    <td className="data-cell font-mono">{h.l2}</td>
                    <td className="data-cell font-mono">{h.selected}</td>
                    <td className="data-cell font-mono">{h.joined}</td>
                    <td className="data-cell font-mono">{h.pending ?? 0}</td>
                    <td className="data-cell font-mono">{h.rejected_noshow ?? 0}</td>
                    <td className="data-cell font-mono font-bold text-[var(--yc-navy)]">{h.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyHRWork;
